import {
  ALLOWED_TRANSITIONS,
  CONTACT_AUTHORITY_LABELS,
  DOC_MODIFIABLE_ACCOUNT_STATUSES,
  DOC_REVIEWABLE_ACCOUNT_STATUSES,
  KYB_SUBMITTABLE_STATUSES,
  REASON_REQUIRED_STATUSES,
} from './kybConstants';

/**
 * Pure helpers behind the KYB screens.
 *
 * Everything the reviewer's buttons and badges depend on lives here rather
 * than inside components, so the rules that matter — the transition map, the
 * required-reason rule, the S3 split, the checklist join — are unit tested
 * instead of being discovered against a live account.
 */

/** `SOME_STATUS` → `Some status`. Falls back to an em dash. */
export const formatEnumLabel = value => {
  if (!value) return '—';
  const spaced = String(value).replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Whether an id is even shaped like an account id.
 *
 * A malformed id makes the server answer with a raw Mongoose cast error —
 * `Cast to ObjectId failed for value ...` — which is not something a reviewer
 * should ever be shown. Checking the shape first keeps that request from
 * being made at all.
 */
export const isLikelyAccountId = id => /^[a-f\d]{24}$/i.test(String(id || ''));

/** Readable copy for `contactAuthorityBasis`. */
export const formatAuthorityBasis = basis =>
  CONTACT_AUTHORITY_LABELS[basis] || formatEnumLabel(basis);

/** Which decisions this account's current status allows. */
export const availableTransitions = kybStatus =>
  ALLOWED_TRANSITIONS[kybStatus] || [];

/** Whether an admin may submit this account for review for the business. */
export const canSubmitOnBehalf = kybStatus =>
  KYB_SUBMITTABLE_STATUSES.includes(kybStatus);

/**
 * A website as a safe external `href`, or null.
 *
 * The server stores it normalised with `https://`, but an older record or a
 * hand edit may lack the scheme — and anything that is not http(s), such as a
 * `javascript:` URL, must never become a clickable link.
 */
export const websiteHref = website => {
  const value = String(website || '').trim();
  if (!value) return null;
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(value)
    ? value
    : `https://${value}`;
  try {
    const url = new URL(withScheme);
    return /^https?:$/.test(url.protocol) ? url.href : null;
  } catch (error) {
    return null;
  }
};

/** One missing checklist row from a refused submission, as a readable line. */
export const describeMissingRequirement = requirement => {
  const label =
    requirement?.label || formatEnumLabel(requirement?.documentType);
  return requirement?.beneficialOwnerName
    ? `${label} — ${requirement.beneficialOwnerName}`
    : label;
};

/** Whether `setKybStatus` will refuse this decision without a reason. */
export const isReasonRequired = status =>
  REASON_REQUIRED_STATUSES.includes(status);

/** Whether accept/reject controls may show for this account's documents. */
export const canReviewDocuments = kybStatus =>
  DOC_REVIEWABLE_ACCOUNT_STATUSES.includes(kybStatus);

/** Whether this account's documents may be replaced or archived. */
export const canModifyDocuments = kybStatus =>
  DOC_MODIFIABLE_ACCOUNT_STATUSES.includes(kybStatus);

/**
 * The queue filter, built from the form state.
 *
 * Every field is optional server-side, and an empty string means "no filter",
 * so blanks are dropped rather than sent through. `ALL` is our own tab key and
 * never reaches the API. Country is matched exactly against the stored ISO
 * code, so it is upper-cased here — a reviewer typing `ci` should still match.
 */
export const buildInstitutionalAccountFilter = ({
  kybStatus,
  country,
  duplicatesOnly,
  search,
  page = 1,
  limit = 10,
} = {}) => ({
  ...(kybStatus && kybStatus !== 'ALL' && {kybStatus}),
  ...(country?.trim() && {
    countryOfIncorporation: country.trim().toUpperCase(),
  }),
  ...(duplicatesOnly && {duplicateRegistrationFlag: true}),
  ...(search?.trim() && {search: search.trim().replace(/\s+/g, ' ')}),
  page,
  limit,
});

/**
 * Splits a raw S3 object URL into the two halves `presignMediaURL` wants.
 *
 * `fileUrl` comes back as a raw object URL on a private bucket, so it 403s in
 * an `href`. Returns null for anything that is not a usable S3 URL — an empty
 * field, a relative path, a bucket-less host — which is the signal to disable
 * the preview control rather than fire a request that cannot succeed.
 */
export const parseS3Url = fileUrl => {
  if (!fileUrl || typeof fileUrl !== 'string') return null;

  let parsed;
  try {
    parsed = new URL(fileUrl);
  } catch (error) {
    return null;
  }

  if (!/^https?:$/.test(parsed.protocol)) return null;

  const host = parsed.hostname;
  const key = decodeURIComponent(parsed.pathname).replace(/^\/+/, '');
  if (!key) return null;

  // Virtual-hosted style: <bucket>.s3.<region>.amazonaws.com/<key>
  const s3HostMatch = host.match(/^(.+?)\.s3[.-]/);
  if (s3HostMatch) return {bucket: s3HostMatch[1], filename: key};

  // Path style: s3.<region>.amazonaws.com/<bucket>/<key>
  if (/^s3[.-]/.test(host) || host === 's3.amazonaws.com') {
    const [bucket, ...rest] = key.split('/');
    if (!bucket || !rest.length) return null;
    return {bucket, filename: rest.join('/')};
  }

  return null;
};

/**
 * The documents proving one checklist row.
 *
 * The row carries ids, not documents; an id with no matching document (a file
 * deleted between fetches, say) is dropped rather than rendered as a blank.
 */
export const resolveRequirementDocuments = (requirement, documents = []) => {
  const ids = requirement?.documentIds || [];
  if (!ids.length) return [];
  const byId = new Map((documents || []).map(doc => [doc?.id, doc]));
  return ids.map(id => byId.get(id)).filter(Boolean);
};

/**
 * Documents not referenced by any checklist row.
 *
 * `OTHER`-typed uploads and extras the business supplied. They are still real
 * files a reviewer must be able to see and rule on, so the file list stays
 * authoritative and the checklist is only a view over it.
 */
export const unlistedDocuments = (documents = [], requirements = []) => {
  const claimed = new Set(
    (requirements || []).flatMap(req => req?.documentIds || []),
  );
  return (documents || []).filter(doc => doc?.id && !claimed.has(doc.id));
};

/**
 * Progress over the mandatory rows.
 *
 * Optional rows are counted separately: an unsatisfied optional row never
 * blocked the submission and should not read as outstanding work.
 */
export const checklistProgress = (requirements = []) => {
  const rows = requirements || [];
  const mandatory = rows.filter(row => row?.mandatory);
  return {
    total: rows.length,
    mandatoryTotal: mandatory.length,
    mandatorySatisfied: mandatory.filter(row => row?.satisfied).length,
    optionalTotal: rows.length - mandatory.length,
    optionalSatisfied: rows.filter(row => !row?.mandatory && row?.satisfied)
      .length,
    allMandatorySatisfied: mandatory.every(row => row?.satisfied),
  };
};

/**
 * What is still standing between this account and being live.
 *
 * Activation has two independent legs — the contact's personal KYC and KYB —
 * and the account exposes no field for the first one. So an approved account
 * that is not yet active can only mean the contact's own KYC has not passed,
 * which is worth saying outright: it is the single most confusing state on
 * this screen, and the fix for it lives in the KYC tooling, not here.
 */
export const activationBlocker = account => {
  if (!account) return null;
  if (account.isFullyActivated) return null;
  if (account.kybStatus === 'APPROVED') {
    return {
      kind: 'CONTACT_KYC',
      message:
        "KYB is approved. The account goes live on its own once the administrator's personal KYC is approved — no further action is needed here.",
    };
  }
  return {kind: 'KYB', message: 'Verification is not approved yet.'};
};

/**
 * Names for the reviewer trail.
 *
 * `statusHistory.actorId` is a raw id with no `actor` field to expand, so the
 * only name available is the reviewer stamped on the account. Anything else
 * renders without a name rather than as a bare ObjectId, which tells a reader
 * nothing.
 */
export const resolveActorName = (actorId, account) => {
  const reviewer = account?.kybReviewer;
  if (actorId && reviewer?.id && actorId === reviewer.id) {
    return `${reviewer.firstName || ''} ${reviewer.lastName || ''}`.trim();
  }
  return null;
};

/** `{firstName, lastName}` → a display name, or an em dash. */
export const personName = person => {
  if (!person) return '—';
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  return name || person.fullName || '—';
};

/**
 * Unwraps a union response.
 *
 * Business failures come back as an `Error` branch on a 200, so an unchecked
 * `data.x.data` silently renders an empty screen instead of surfacing the
 * message. Returns `{data, error}` — exactly one of which is set.
 */
export const unwrap = payload => {
  if (!payload) return {data: null, error: null};
  if (payload.__typename === 'Error') {
    return {data: null, error: payload.message || 'Something went wrong'};
  }
  return {data: payload.data ?? null, error: null};
};
